import { differenceInCalendarDays, parseISO } from 'date-fns';
import {
    ClaimWorkflowHistoryItem,
    ProviderClaim,
    ProviderClaimDetails,
    ProviderProfessionalClaim,
    ProviderProfessionalClaimTemplate,
    ProviderType,
} from '../../api';

/**
 * What the claim drawer reads off one claim, for a denial (FE-116, design 05).
 *
 * ## Which claim
 *
 * The drawer opens from a payment in the drill-down, and the payment does NOT name its claim: it has
 * no `claimId`, and its `professionalId` is the id of the professional-claim SUB-RECORD. Measured on
 * production 2026-10-01, `/provider/claim/{id}`, `/details` and `/worker/history` all answer 500 for
 * that id and 200 for the claim's own id, which comes from `/provider/claim?filterPaymentId=`. The
 * store resolves it that way; nothing here guesses an id.
 *
 * ## What is read from where
 *
 * - **CPT** — the denied lines of the payment itself (`IDenialPaymentRow.procedureCodes`), so the
 *   chips name the lines that were denied rather than every line on the claim.
 * - **ICD and NPI** — the latest professional claim in `details`. Institutional and dental claims have
 *   a different form and show neither; that is said in the drawer, not left blank.
 * - **Activity** — the claim's worker history, newest first.
 *
 * Balance and the timely-filing deadline are NOT here: `ProviderClaim` has no balance field, and
 * nothing in the API states a payer's filing limit. The drawer shows both disabled, with the reason
 * (NS-4).
 */

/** Box 21, A through L, in form order. The order matters: service lines point at codes by letter. */
// Spelled out rather than built from letters, so a field renamed in the client fails to compile here.
const DIAGNOSIS_FIELDS: Array<keyof ProviderProfessionalClaimTemplate> = [
    'f21aDiagnosisCode',
    'f21bDiagnosisCode',
    'f21cDiagnosisCode',
    'f21dDiagnosisCode',
    'f21eDiagnosisCode',
    'f21fDiagnosisCode',
    'f21gDiagnosisCode',
    'f21hDiagnosisCode',
    'f21iDiagnosisCode',
    'f21jDiagnosisCode',
    'f21kDiagnosisCode',
    'f21lDiagnosisCode',
];

/**
 * The professional claim as it stands now.
 *
 * A claim carries one professional record per version — a resubmission adds one — so the newest is
 * the one the payer last saw. `created` is an ISO string; one with no date sorts last.
 */
export const latestProfessional = (
    details: ProviderClaimDetails | undefined,
): ProviderProfessionalClaim | undefined =>
    [...(details?.professionals || [])].sort((a, b) => (b.created || '').localeCompare(a.created || ''))[0];

/** The ICD-10 codes in box 21, as recorded. Not reformatted: a dot added here would be a guess. */
export const claimDiagnosisCodes = (details: ProviderClaimDetails | undefined): string[] => {
    const template = latestProfessional(details)?.professionalTemplate;
    if (!template) {
        return [];
    }
    const codes = DIAGNOSIS_FIELDS.map(field => template[field])
        .map(value => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean);

    return [...new Set(codes)];
};

/**
 * The RENDERING provider's NPI — the clinician on the line, which is the one a denial is about.
 *
 * Not the billing NPI as a fallback: the two answer different questions, and a chip reading "NPI"
 * that silently switched meaning between claims would be wrong on exactly the claims that differ.
 */
export const renderingNpi = (details: ProviderClaimDetails | undefined): string | undefined =>
    (latestProfessional(details)?.providers || []).find(
        provider => provider.type === ProviderType.RENDERING && provider.npi,
    )?.npi;

/** `Osei, Abena` — the order the design and the claims list use. Suffix after the first name. */
export const claimPatientName = (claim: ProviderClaim | undefined): string => {
    const last = (claim?.patientLast || '').trim();
    const first = [claim?.patientFirst, claim?.patientSuffix].map(part => (part || '').trim()).filter(Boolean).join(' ');

    return [last, first].filter(Boolean).join(', ') || 'Unnamed patient';
};

/**
 * Whole days since the remittance that carried the denial was received, falling back to its payment
 * date. That is what the design's "Age" counts down from: how long this denial has been sitting.
 * `undefined` when the payment carries neither date — never zero, which would read as "today".
 */
export const denialAgeDays = (
    payment: { receivedDate?: string; paymentDate?: string },
    now: Date,
): number | undefined => {
    const from = payment.receivedDate || payment.paymentDate;
    if (!from) {
        return undefined;
    }
    const days = differenceInCalendarDays(now, parseISO(from));

    return Number.isFinite(days) ? Math.max(0, days) : undefined;
};

export interface IClaimActivity {
    key: string;
    /** What happened — `Sent Claim Send Request`. */
    title: string;
    /** The workflow step it happened in, when that adds anything to the title. */
    step?: string;
    /** The workflow status the claim was left in. */
    status?: string;
    date?: string;
}

/** The worker history, newest first. The endpoint returns it in no stated order. */
export const claimActivity = (history: ClaimWorkflowHistoryItem[] | undefined): IClaimActivity[] =>
    [...(history || [])]
        .sort((a, b) => (b.actionDate || '').localeCompare(a.actionDate || ''))
        .map((item, index) => {
            const title = item.actionLabel || item.action || 'Worker action';
            const step = item.workflowStepLabel && item.workflowStepLabel !== title ? item.workflowStepLabel : undefined;

            return {
                key: `${item.actionDate || ''}:${index}`,
                title,
                step,
                status: item.workflowStatusLabel || undefined,
                date: item.actionDate,
            };
        });
